import React, { useState, useCallback, useRef, useEffect } from 'react';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import QrCodeScannerOutlinedIcon from '@mui/icons-material/QrCodeScannerOutlined';
import { BarcodeStatus } from '../../types';

// ===== Barcode Status Badge =====

interface BarcodeStatusBadgeProps {
  status: BarcodeStatus;
}

export const BarcodeStatusBadge: React.FC<BarcodeStatusBadgeProps> = ({ status }) => {
  if (status === 'unconfirmed') {
    return (
      <span title="Barcode unconfirmed" style={{ display: 'inline-flex', alignItems: 'center' }}>
        <HelpOutlineIcon style={{ fontSize: 14, color: 'var(--text-tertiary)' }} />
      </span>
    );
  }

  const isConfirmed = status === 'confirmed';

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        fontSize: 10,
        fontWeight: 500,
        padding: '2px 6px',
        borderRadius: 'var(--radius-sm)',
        background: isConfirmed ? 'var(--positive-subtle)' : 'var(--warning-subtle)',
        color: isConfirmed ? 'var(--positive)' : 'var(--warning)',
      }}
    >
      {isConfirmed ? (
        <CheckCircleOutlineIcon style={{ fontSize: 14 }} />
      ) : (
        <ScheduleOutlinedIcon style={{ fontSize: 14 }} />
      )}
      {isConfirmed ? 'Confirmed' : 'Pending'}
    </span>
  );
};

// ===== Request Verification Button =====

interface RequestVerificationButtonProps {
  status: BarcodeStatus;
  variantId: string;
  onRequest?: (variantId: string) => void;
}

export const RequestVerificationButton: React.FC<RequestVerificationButtonProps> = ({
  status,
  variantId,
  onRequest,
}) => {
  const [requested, setRequested] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handleClick = useCallback(() => {
    onRequest?.(variantId);
    setRequested(true);
    timerRef.current = setTimeout(() => setRequested(false), 2000);
  }, [onRequest, variantId]);

  if (requested) {
    return (
      <span style={{ fontSize: 11, color: 'var(--positive)', fontWeight: 500 }}>
        Verification requested
      </span>
    );
  }

  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    fontWeight: 500,
    padding: '4px 8px',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    cursor: 'pointer',
    lineHeight: 1,
  };

  if (status === 'pending') {
    return (
      <button disabled style={{ ...base, background: 'var(--warning-subtle)', color: 'var(--warning)', opacity: 0.7, cursor: 'not-allowed' }}>
        Verification Pending
      </button>
    );
  }

  if (status === 'confirmed') {
    return (
      <button onClick={handleClick} style={{ ...base, background: 'transparent', color: 'var(--text-secondary)', textDecoration: 'underline' }}>
        Re-verify
      </button>
    );
  }

  return (
    <button onClick={handleClick} style={{ ...base, background: 'var(--accent-subtle)', color: 'var(--accent)', border: '1px solid var(--accent-border)' }}>
      <QrCodeScannerOutlinedIcon style={{ fontSize: 14 }} />
      Verify Barcode
    </button>
  );
};
